import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { decode } from 'html-entities'
import { decodeAllStrings } from '@utils/decoder'
import getCSRFToken from '@utils/csrf'
import ErrorTypes from '@constants/error_types'
import { createLogger } from '@utils/logger'

const log = createLogger('Custom Axios', { prependScope: true })
log.setLevel('info')

axios.defaults.headers.common['X-CSRF-Token'] = getCSRFToken()

// A map to keep track of in-progress API calls
const inProgressAPICalls: Record<string, boolean> = {}

/**
 * @name makeRequest
 * @description: Reusable method to perform axios requests
 * @param requestData
 * @param requestData.url The endpoint URL
 * @param requestData.method The API request method in lowercase e.g., 'get', 'post', 'delete', etc.
 * @param requestData.data The optional payload
 * @param requestData.requestId The unique identifier for the request
 * @returns Promise
 */
interface RequestData {
  url: string
  method: 'get' | 'post' | 'put' | 'delete' | 'patch'
  data?: any
  params?: Record<string, any>
  transformResponse?: any // TODO: Add proper type for the transform function
  requestId: string
  headers?: any // TODO: Add proper type for this
}

/**
 * Clear in-progress API call
 * @param requestId
 */
const clearInProgressCall = (requestId: string): void => {
  delete inProgressAPICalls[requestId]
}

// A passthrough transform if you need to disable auto-parse to JSON functionality for some reason
export const rawResponseTransform = (data: string) => data

// A simple decode and JSON.parse transform
// Useful for when you need to decode HTML entities before parsing JSON
// We use the Sanitize gem which encodes HTML entities when serializing JSON
export const parseAndDecodeTransformResponse = (data: string) => {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      const decoded = decodeAllStrings(parsed)
      return decoded
    } catch (error) {
      return decode(data)
    }
  } else if (typeof data === 'object' && data !== null) {
    // If data is already parsed by axios, just decode it
    return decodeAllStrings(data)
  } else {
    return data
  }
}

/**
 * Reusable axios request function
 * @param requestData - Object containing URL, method, data, params, transformResponse, and requestId
 */
export const makeRequest = async ({
  url,
  method,
  data = undefined,
  params = {},
  transformResponse = parseAndDecodeTransformResponse,
  requestId,
  headers,
}: RequestData): Promise<AxiosResponse> => {
  if (inProgressAPICalls[requestId]) {
    return Promise.reject(new Error(ErrorTypes.CALL_ALREADY_IN_PROGRESS))
  }

  // Set default JSON headers
  const requestHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...headers, // Allow custom headers to overwrite defaults
  }

  // Mark the request as in progress
  inProgressAPICalls[requestId] = true

  try {
    // Axios request configuration
    const config: AxiosRequestConfig = {
      url,
      method,
      params: method === 'get' ? params : undefined,
      data: method !== 'get' ? data : undefined,
      transformResponse,
      headers: requestHeaders,
    }
    log.debug('Making Axios request', config)
    return await axios(config)
  } catch (error) {
    throw error // Re-throw the error to be handled by the caller
  } finally {
    clearInProgressCall(requestId)
  }
}

// Ensures we add the correct content type header for JSON:API
export const makeJsonApiRequest = async (requestData: RequestData): Promise<AxiosResponse> => {
  const jsonApiContentType = { 'Content-Type': 'application/vnd.api+json' }
  return makeRequest({
    ...requestData,
    headers: {
      ...jsonApiContentType,
      ...requestData.headers,
    },
  })
}

export default axios
